import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BPSAdminLevel, PermissionsEnum, PermissionsService } from 'shared-lib';
import {
  MeterAssignment,
  feederBandOptions,
  meterTypeOptions,
  projectTypeOptions,
  serviceRequestTypeOptions,
} from 'src/app/models/meterAssignment.model';
import { MeterAssignmentService } from 'src/app/services/meter-assignment.service';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';
import { ProjectDesignationService } from 'src/app/services/project-designation.service';

@Component({
  selector: 'crud-meter-assignment',
  templateUrl: './crud-meter-assignment.component.html',
  styleUrls: ['./crud-meter-assignment.component.scss'],
})
export class CrudMeterAssignmentComponent implements OnInit {
  @Input() searchText: string = '';
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;

  @ViewChild('closeAddEditModal') closeAddEditModal!: ElementRef;
  @ViewChild('closeDeleteModal') closeDeleteModal!: ElementRef;
  @ViewChild('openEditModalBtn') openEditModalBtn!: ElementRef;

  public PermissionsEnum = PermissionsEnum;

  currentPage = 1;
  pageSize = 10;
  count = 0;
  clicked: boolean = false;

  meterAssignments: MeterAssignment[] = [];
  selectedItem: MeterAssignment = new MeterAssignment();
  isEditMode = false;
  globalIsDisabled = false;

  projectTypes = projectTypeOptions;
  selectedProjectType: number | undefined;

  projectDesignations: { id: number; name: string }[] = [];
  selectedProjectDesignationId: number | null = null;

  feederBandItems: { id: number; name: string; checked: boolean }[] = [];
  serviceRequestTypeItems: { id: number; name: string; checked: boolean }[] = [];
  meterTypeItems: { id: number; name: string; checked: boolean }[] = [];
  organizationItems: { id: number; name: string; checked: boolean }[] = [];

  constructor(
    private toastr: ToastrService,
    public permissionsService: PermissionsService,
    private meterAssignmentService: MeterAssignmentService,
    private projectDesignationService: ProjectDesignationService,
    private organizationService: OrganizationTeamApiService
  ) {}

  ngOnInit(): void {
    this.resetMultiSelectItems();
    this.loadDropdownData();
    this.reloadTable();
  }

  private loadDropdownData(): void {
    this.projectDesignationService.getAllForSelect(this.utilityId || null).subscribe({
      next: (res) => {
        if (res?.body?.data) {
          this.projectDesignations = res.body.data;
        }
      },
    });

    this.organizationService.getAllForSelect().subscribe({
      next: (res: any) => {
        const data = res?.data || res?.body?.data || res;
        if (Array.isArray(data)) {
          this.organizationItems = data.map((o: any) => ({ id: o.id, name: o.name, checked: false }));
        }
      },
    });
  }

  private resetMultiSelectItems(): void {
    this.feederBandItems = feederBandOptions.map((o) => ({ ...o, checked: false }));
    this.serviceRequestTypeItems = serviceRequestTypeOptions.map((o) => ({ ...o, checked: false }));
    this.meterTypeItems = meterTypeOptions.map((o) => ({ ...o, checked: false }));
  }

  reloadTable(page: number | null = null): void {
    if (page) this.currentPage = page;
    const dataIn = {
      pageInfo: { page: this.currentPage, pageSize: this.pageSize },
      utilityId: this.utilityId,
    };
    this.meterAssignmentService.getAll(dataIn).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.meterAssignments = response.data.data;
          this.count = response.data.count;
          this.globalIsDisabled = this.meterAssignments.length > 0 && this.meterAssignments.every((x) => x.isDisabled);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  filter(): void {
    this.reloadTable(1);
  }

  pageChange(value: number): void {
    this.currentPage = value;
    this.reloadTable();
  }

  addNew(): void {
    this.isEditMode = false;
    this.selectedItem = new MeterAssignment();
    this.selectedProjectType = undefined;
    this.selectedProjectDesignationId = null;
    this.resetMultiSelectItems();
    this.organizationItems = this.organizationItems.map((o) => ({ ...o, checked: false }));
    this.clicked = !this.clicked;
  }

  openEditModal(item: MeterAssignment): void {
    this.selectItem(item);
    this.openEditModalBtn.nativeElement.click();
  }

  selectItem(item: MeterAssignment): void {
    this.isEditMode = true;
    this.selectedItem = { ...item };
    this.selectedProjectType = item.projectType;
    this.selectedProjectDesignationId = item.projectDesignationId;
    this.feederBandItems = feederBandOptions.map((o) => ({ ...o, checked: item.feederBands.includes(o.id) }));
    this.serviceRequestTypeItems = serviceRequestTypeOptions.map((o) => ({
      ...o,
      checked: item.serviceRequestTypes.includes(o.id),
    }));
    this.meterTypeItems = meterTypeOptions.map((o) => ({ ...o, checked: item.meterTypes.includes(o.id) }));
    this.organizationItems = this.organizationItems.map((o) => ({ ...o, checked: item.organizationIds.includes(o.id) }));
    this.clicked = !this.clicked;
  }

  onModalFade(): void {
    this.selectedItem = new MeterAssignment();
    this.selectedProjectType = undefined;
    this.selectedProjectDesignationId = null;
    this.resetMultiSelectItems();
  }

  onFeederBandChange(event: any): void {
    const item = this.feederBandItems.find((x) => x.id === event.id);
    if (item) item.checked = event.checked;
  }

  onServiceRequestTypeChange(event: any): void {
    const item = this.serviceRequestTypeItems.find((x) => x.id === event.id);
    if (item) item.checked = event.checked;
  }

  onMeterTypeChange(event: any): void {
    const item = this.meterTypeItems.find((x) => x.id === event.id);
    if (item) item.checked = event.checked;
  }

  onOrganizationChange(event: any): void {
    const item = this.organizationItems.find((x) => x.id === event.id);
    if (item) item.checked = event.checked;
  }

  isValid(): boolean {
    return (
      this.selectedProjectType !== undefined &&
      this.feederBandItems.some((x) => x.checked) &&
      this.serviceRequestTypeItems.some((x) => x.checked) &&
      this.meterTypeItems.some((x) => x.checked) &&
      this.organizationItems.some((x) => x.checked)
    );
  }

  save(): void {
    if (!this.isValid()) {
      this.toastr.warning('Please fill all required fields.');
      return;
    }
    const dataIn = {
      id: this.selectedItem.id,
      utilityId: this.utilityId,
      projectType: this.selectedProjectType,
      projectDesignationId: this.selectedProjectDesignationId,
      feederBands: this.feederBandItems.filter((x) => x.checked).map((x) => x.id),
      serviceRequestTypes: this.serviceRequestTypeItems.filter((x) => x.checked).map((x) => x.id),
      meterTypes: this.meterTypeItems.filter((x) => x.checked).map((x) => x.id),
      organizationIds: this.organizationItems.filter((x) => x.checked).map((x) => x.id),
    };
    this.meterAssignmentService.save(dataIn).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.closeAddEditModal?.nativeElement?.click();
          this.toastr.success(response.message);
          this.reloadTable(this.isEditMode ? null : 1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  deleteItem(): void {
    this.meterAssignmentService.delete(this.selectedItem.id).subscribe({
      next: (res) => {
        const response = res?.body;
        this.closeDeleteModal?.nativeElement?.click();
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success(response.message);
          this.reloadTable(1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  toggleStatus(item: MeterAssignment, event: MouseEvent): void {
    event.stopPropagation();
    this.meterAssignmentService.toggleStatus(item.id).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          item.isDisabled = !item.isDisabled;
          this.globalIsDisabled = this.meterAssignments.every((x) => x.isDisabled);
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  toggleAll(): void {
    const newState = !this.globalIsDisabled;
    this.meterAssignmentService.toggleAll(newState).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.globalIsDisabled = newState;
          this.meterAssignments.forEach((x) => (x.isDisabled = newState));
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  getProjectTypeName(id: number | undefined): string {
    return projectTypeOptions.find((x) => x.id === id)?.name ?? '-';
  }

  getProjectDesignationName(id: number | null): string {
    return this.projectDesignations.find((x) => x.id === id)?.name ?? '-';
  }

  getFeederBandNames(ids: number[]): string {
    return ids.map((id) => feederBandOptions.find((x) => x.id === id)?.name).filter(Boolean).join(', ') || '-';
  }

  getServiceRequestTypeNames(ids: number[]): string {
    return ids.map((id) => serviceRequestTypeOptions.find((x) => x.id === id)?.name).filter(Boolean).join(', ') || '-';
  }

  getMeterTypeNames(ids: number[]): string {
    return ids.map((id) => meterTypeOptions.find((x) => x.id === id)?.name).filter(Boolean).join(', ') || '-';
  }

  getOrganizationNames(ids: number[]): string {
    return ids.map((id) => this.organizationItems.find((x) => x.id === id)?.name).filter(Boolean).join(', ') || '-';
  }
}
